import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    IconButton,
    Chip,
    Typography,
    Box,
    Tooltip,
    CircularProgress
} from '@mui/material';
import { FaCheck, FaEdit, FaTrash, FaSave, FaTimes, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const TimeEntriesTable = ({
    loading = false,
    filteredEntries = [],
    selectedEntries = [],
    handleSelectAll,
    handleSelectEntry,
    approveEntry,
    handleDeleteEntry, // New delete function
    setEditingEntry,
    startInlineEdit,
    handleInlineChange,
    saveInlineEdit,
    cancelInlineEdit,
    inlineEditing,
    projects = [],
    scaffoldIds = [],
    searchTerm = '',
    totalHours = '0.00',
    sortConfig = {},
    onSortChange
}) => {
    const isAllSelected = filteredEntries.length > 0 && selectedEntries.length === filteredEntries.length;
    const isIndeterminate = selectedEntries.length > 0 && selectedEntries.length < filteredEntries.length;

    const handleSort = (field) => {
        const direction = sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        onSortChange({ field, direction });
    };

    const getSortIcon = (field) => {
        if (sortConfig.field !== field) {
            return <FaSort style={{ fontSize: '12px', opacity: 0.5 }} />;
        }
        return sortConfig.direction === 'asc' 
            ? <FaSortUp style={{ fontSize: '12px', color: '#1976d2' }} />
            : <FaSortDown style={{ fontSize: '12px', color: '#1976d2' }} />;
    };

    const highlightText = (text, searchTerm) => {
        if (!searchTerm || !text) return text;
        
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) =>
            regex.test(part) ? (
                <span key={index} style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={isIndeterminate}
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                    disabled={filteredEntries.length === 0}
                                />
                            </TableCell>
                            <TableCell 
                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('userId.name')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    User
                                    {getSortIcon('userId.name')}
                                </Box>
                            </TableCell>
                            <TableCell 
                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('date')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Date
                                    {getSortIcon('date')}
                                </Box>
                            </TableCell>
                            <TableCell 
                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('projectId.projectName')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Project
                                    {getSortIcon('projectId.projectName')}
                                </Box>
                            </TableCell>
                            <TableCell>Scaffold ID</TableCell>
                            <TableCell 
                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('totalHours')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Hours
                                    {getSortIcon('totalHours')}
                                </Box>
                            </TableCell>
                            <TableCell>Work Type</TableCell>
                            <TableCell>Additional Compensation</TableCell>
                            <TableCell 
                                sx={{ cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => handleSort('approvalStatus')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Status
                                    {getSortIcon('approvalStatus')}
                                </Box>
                            </TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredEntries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No time entries found
                                        {searchTerm && ` matching "${searchTerm}"`}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredEntries.map((entry) => (
                                <TableRow 
                                    key={entry._id}
                                    hover
                                    sx={{ 
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                        backgroundColor: entry.approvalStatus === 'approved' 
                                            ? 'rgba(76, 175, 80, 0.1)' 
                                            : 'inherit'
                                    }}
                                >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedEntries.includes(entry._id)}
                                            onChange={(e) => handleSelectEntry(entry._id, e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {highlightText(entry?.userId?.name || '-', searchTerm)}
                                    </TableCell>
                                    <TableCell>
                                        {highlightText(formatDate(entry.date), searchTerm)}
                                    </TableCell>
                                    <TableCell>
                                        {highlightText(entry?.projectId?.projectName || '-', searchTerm)}
                                    </TableCell>
                                    <TableCell>
                                        {highlightText(entry?.scaffoldId?.scaffoldId || '-', searchTerm)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                            {entry.totalHours || '0'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {highlightText(entry?.workTypeId?.name || '-', searchTerm)}
                                    </TableCell>
                                    <TableCell>
                                        {entry?.compensationTypeId?.name 
                                            ? highlightText(entry.compensationTypeId.name, searchTerm)
                                            : '-'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={entry.approvalStatus === 'approved' ? 'Approved' : 'Pending'}
                                            color={entry.approvalStatus === 'approved' ? 'success' : 'warning'}
                                            size="small"
                                            sx={{ minWidth: '80px' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {entry.approvalStatus !== 'approved' && (
                                                <Tooltip title="Approve Entry">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => approveEntry(entry._id)}
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <FaCheck size={14} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            
                                            <Tooltip title="Edit Entry">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => setEditingEntry(entry)}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <FaEdit size={14} />
                                                </IconButton>
                                            </Tooltip>

                                            {entry.approvalStatus !== 'approved' && (
                                                <Tooltip title="Delete Entry">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteEntry(entry)}
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <FaTrash size={14} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredEntries.length > 0 && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 1
                }}>
                    <Typography variant="body2" color="textSecondary">
                        {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} found
                        {searchTerm && ` matching "${searchTerm}"`}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                        Total Hours: {totalHours}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default TimeEntriesTable;